/** RFC 7807 problem+json as rendered by apps/api/tibyan/core/errors.py. */
export interface ProblemFieldError {
  field: string;
  code: string;
  message: string;
}

export interface ProblemBody {
  type: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  instance?: string | null;
  errors?: ProblemFieldError[];
  [extension: string]: unknown;
}

/** Client-side codes for failures that never produced a problem document. */
export const CLIENT_CODES = {
  network: 'NETWORK_ERROR',
  invalidResponse: 'INVALID_RESPONSE',
} as const;

const CORE_KEYS = new Set(['type', 'title', 'status', 'code', 'detail', 'instance', 'errors']);

export class ApiProblem extends Error {
  readonly status: number;
  readonly code: string;
  readonly type: string;
  readonly title: string;
  readonly detail: string | undefined;
  readonly instance: string | null | undefined;
  readonly errors: ProblemFieldError[];
  readonly extensions: Record<string, unknown>;

  constructor(body: ProblemBody) {
    super(body.detail ?? (body.title || body.code));
    this.name = 'ApiProblem';
    this.status = body.status;
    this.code = body.code;
    this.type = body.type;
    this.title = body.title;
    this.detail = body.detail;
    this.instance = body.instance;
    this.errors = body.errors ?? [];
    this.extensions = Object.fromEntries(Object.entries(body).filter(([key]) => !CORE_KEYS.has(key)));
  }

  /** Field errors keyed by field path ("organization.slug"). First message per field wins. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.errors) {
      if (!(e.field in out)) out[e.field] = e.message;
    }
    return out;
  }

  is(code: string): boolean {
    return this.code === code;
  }
}

export function isApiProblem(value: unknown): value is ApiProblem {
  return value instanceof ApiProblem;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toFieldErrors(value: unknown): ProblemFieldError[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: ProblemFieldError[] = [];
  for (const item of value) {
    if (isRecord(item) && typeof item.field === 'string') {
      out.push({
        field: item.field,
        code: typeof item.code === 'string' ? item.code : 'invalid',
        message: typeof item.message === 'string' ? item.message : '',
      });
    }
  }
  return out;
}

/** Narrow an unknown JSON payload into a ProblemBody, filling gaps from the HTTP status. */
export function toProblemBody(payload: unknown, status: number, statusText = ''): ProblemBody {
  if (isRecord(payload) && typeof payload.code === 'string') {
    const errors = toFieldErrors(payload.errors);
    const body: ProblemBody = {
      ...payload,
      type: typeof payload.type === 'string' ? payload.type : 'about:blank',
      title: typeof payload.title === 'string' ? payload.title : statusText,
      status: typeof payload.status === 'number' ? payload.status : status,
      code: payload.code,
    };
    if (typeof payload.detail === 'string') body.detail = payload.detail;
    else delete body.detail;
    if (errors) body.errors = errors;
    else delete body.errors;
    return body;
  }
  return {
    type: 'about:blank',
    title: statusText,
    status,
    code: status === 0 ? CLIENT_CODES.network : `HTTP_${status}`,
  };
}
